import {PRE_SIGN_MEDIA_URL} from 'graphql/mutations/wallet';
import {client} from 'index';
import {formatFileName} from 'utils/helper';
import {ICON_BUCKET} from './constants';

/** Pull rows + error out of a `Response* | Error` union node. */
export const unwrap = node => {
  if (!node) return {error: null, rows: []};
  if (node.__typename === 'Error') return {error: node, rows: []};
  return {error: null, rows: node.data || []};
};

/** Read the `Error.message` out of a union response, if any. */
export const errorMessageFrom = (data, key, fallbackError) => {
  const node = data?.[key];
  if (node?.__typename === 'Error') return node.message;
  return fallbackError?.message;
};

/**
 * Folder-aware upload that does NOT set the public flag on the presign call.
 * Returns the resulting S3 URL on success.
 */
export const uploadIconToBucket = async (file, folder) => {
  const safeName = formatFileName(file.name);
  const filename = `${folder}/${safeName}`;

  const {data} = await client.mutate({
    mutation: PRE_SIGN_MEDIA_URL,
    variables: {
      input: {
        bucket: ICON_BUCKET,
        upload: true,
        filename,
        contentType: file?.type,
      },
    },
  });

  if (data?.presignMediaURL?.statusCode === 400) {
    throw new Error(data?.presignMediaURL?.message || 'Presign failed');
  }
  const presignedUrl = data?.presignMediaURL?.data?.url;
  if (!presignedUrl) throw new Error('Missing presigned URL');

  const res = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {'Content-Type': file?.type},
    body: file,
    redirect: 'follow',
  });
  if (!res.ok) throw new Error('Upload failed');

  return `https://${ICON_BUCKET}.s3.us-east-1.amazonaws.com/${filename}`;
};

// Legacy camelCase resolver names (e.g. "getUserDepositTransactionsContext")
// → enum form ("GET_USER_DEPOSIT_TRANSACTIONS_CONTEXT"). Already-uppercase
// values pass through unchanged.
const normaliseResolver = value => {
  if (!value) return value;
  if (/^[A-Z0-9_]+$/.test(value)) return value;
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toUpperCase();
};

const normaliseStepType = value =>
  value ? String(value).toUpperCase() : value;

// Parse a payload that may arrive as a JSON string (legacy) or an object.
const parsePayload = value => {
  if (value == null) return {};
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return {};
    try {
      return JSON.parse(trimmed);
    } catch {
      return {};
    }
  }
  return {};
};

// Strip empty / null / undefined values (incl. empty arrays / objects) so the
// saved payload only carries what the admin actually set.
const stripEmptyPayload = obj => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v;
      return;
    }
    if (typeof v === 'object') {
      const inner = stripEmptyPayload(v);
      if (Object.keys(inner).length) out[k] = inner;
      return;
    }
    out[k] = v;
  });
  return out;
};

/**
 * Map a raw issue record into form values. Payload is kept as an OBJECT so the
 * typed filter form can hydrate from it (preserving any unknown legacy keys),
 * and legacy resolver / type values are uppercased so the catalog dropdowns match.
 */
export const issueRecordToFormValues = record => ({
  title: record.title,
  stepTemplate: record.stepTemplate,
  skillId: record.skillId || undefined,
  faqPostIds: (record.faqPostIds || []).map(String),
  sortOrder: record.sortOrder ?? 0,
  isActive: !!record.isActive,
  availableToAnonymous: !!record.availableToAnonymous,
  fallthrough: !!record.fallthrough,
  contextConfig: (record.contextConfig || []).map(step => ({
    ...step,
    resolver: normaliseResolver(step?.resolver),
    type: normaliseStepType(step?.type),
    payload: parsePayload(step?.payload),
  })),
});

/**
 * Build the GraphQL input from form values.
 * Throws when a context step's payload is invalid JSON.
 */
export const buildIssueInput = ({
  values,
  isEdit,
  recordId,
  featureId,
  language,
}) => {
  const faqPostIds = (values.faqPostIds || [])
    .map(v => Number(v))
    .filter(n => Number.isFinite(n));

  const contextConfig = (values.contextConfig || []).map((step, idx) => {
    if (!step?.key || !step?.resolver || !step?.type) {
      throw new Error(
        `Step ${
          idx + 1
        } is incomplete — key, resolver, and type are all required.`,
      );
    }
    // Payload is an object from the typed filter form (or legacy JSON string).
    const parsed = stripEmptyPayload(parsePayload(step.payload));
    return {
      key: step.key,
      // ContextStepType enum — always uppercase.
      type: normaliseStepType(step.type),
      dependsOn: step.dependsOn || null,
      // ContextResolver enum — accept either form coming in.
      resolver: normaliseResolver(step.resolver),
      payload: Object.keys(parsed).length ? parsed : null,
    };
  });

  const base = {
    title: values.title,
    stepTemplate: values.stepTemplate,
    skillId: values.skillId || null,
    faqPostIds,
    sortOrder: Math.max(1, Number(values.sortOrder) || 1),
    isActive: !!values.isActive,
    // CONTEXT_SELECT can never be anonymous — its resolvers need a userId.
    availableToAnonymous:
      values.stepTemplate === 'CONTEXT_SELECT'
        ? false
        : !!values.availableToAnonymous,
    // Independent of stepTemplate — any template can be escalate-first; the
    // template only shapes the steps shown before the hand-off to a human.
    fallthrough: !!values.fallthrough,
    contextConfig:
      values.stepTemplate === 'CONTEXT_SELECT' ? contextConfig : [],
  };

  return isEdit ? {id: recordId, ...base} : {featureId, language, ...base};
};

// Humanise an enum tree value: PENDING_WITHDRAWAL → "Pending Withdrawal".
const humaniseTree = value =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/**
 * Turn the skills query data into Select options. Filters to PUBLISHED/DRAFT
 * SUPPORT-domain skills only (helpdesk flows are SUPPORT-only) and humanises
 * the tree portion of the label so it reads naturally.
 */
export const toSkillOptions = skillsData => {
  const node = skillsData?.getSkills;
  const rows = node?.__typename === 'ResponseWithSkills' ? node.data : [];
  return (
    (rows || [])
      // Guard against null entries: a skill whose node/tree value isn't in the
      // GraphQL SkillNode!/SkillTree! enums fails to serialize and arrives as
      // null, which would otherwise crash the whole flow-builder on `s.status`.
      .filter(s => s && s.status !== 'ARCHIVED' && s.domain === 'SUPPORT')
      .map(s => {
        const tree = humaniseTree(s.tree);
        const path = [s.node, tree].filter(Boolean).join(' / ');
        const draftTag = s.status === 'DRAFT' ? ' (Draft)' : '';
        const label = `${path || s.id}${draftTag}`;
        return {value: s.id, label};
      })
  );
};
